import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { AIRPORTS, FBOS, SITE, CITIES, SERVICES } from "@/lib/site-data";
import { CTABand } from "@/components/CTA";
import { CrossLinkModule } from "@/components/CrossLinks";
import { Building2, Plane, Phone, MapPin, Navigation, Users, Car, Luggage, ShieldAlert, Mail } from "lucide-react";
import { breadcrumbJsonLd } from "@/lib/breadcrumbs";
import { ldJson, fboFaqs, faqJsonLd, hoursSpec24x7, speakableJsonLd } from "@/lib/seo-schema";
import { FBO_EXTENDED_CONTENT } from "@/lib/fbo-content";
import { FBO_GALLERY, fboGalleryJsonLd } from "@/lib/fbo-gallery";
import { GalleryLightbox } from "@/components/GalleryLightbox";
import { FBO_LOCATIONS, fboMapEmbedUrl, fboDirectionsUrl } from "@/lib/fbo-locations";
import { FBO_PICKUP } from "@/lib/fbo-pickup";

// Compact FBO display names used in <title>, og:title, and JSON-LD to keep the
// SERP title width under Google's ~580px display limit while retaining the
// FBO name + airport code keywords.
const FBO_SHORT_NAMES: Record<string, string> = {
  "signature-sjc": "Signature Aviation SJC",
  "atlantic-sjc": "Atlantic Aviation SJC",
  "signature-sfo": "Signature Flight Support SFO",
  "moffett-field": "Moffett Field",
  "signature-oak": "Signature Aviation OAK",
  "monterey-jet-center": "Monterey Jet Center (MRY)",
};

export const Route = createFileRoute("/fbos/$slug")({
  loader: ({ params }) => {
    const fbo = FBOS.find((f) => f.slug === params.slug);
    if (!fbo) throw notFound();
    return { fbo };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "FBO not found" }, { name: "robots", content: "noindex" }] };
    }
    const f = loaderData.fbo;
    // Short, unique display name that keeps <title> under ~580px on Google SERPs
    // while retaining the FBO + airport code keywords.
    const shortName = FBO_SHORT_NAMES[f.slug] ?? f.name;
    const title = `${shortName} Chauffeur Service | Premier Executive Chauffeur`;
    const shortTitle = `${shortName} Chauffeur Service`;
    const desc = `Flight-tracked, ramp-side chauffeur at ${shortName} (${f.city}) — crew-coordinated pickups, flat rates, Bay Area dispatch.`;
    const url = `https://premierexecutivechauffeur.com/fbos/${f.slug}`;
    const gallery = FBO_GALLERY[f.slug];
    const heroImage = gallery?.[0];
    const heroImageUrl = heroImage
      ? (heroImage.src.startsWith("http") ? heroImage.src : `https://premierexecutivechauffeur.com${heroImage.src}`)
      : "https://premierexecutivechauffeur.com/og-default.jpg";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: shortTitle },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:image", content: heroImageUrl },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: shortTitle },
        { name: "twitter:description", content: desc },
        { name: "twitter:image", content: heroImageUrl },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Airports & FBOs", path: "/airports" },
          { name: shortName, path: url },
        ]),
        {
          type: "application/ld+json",
          children: ldJson({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": `${url}#webpage`,
            url,
            name: shortTitle,
            description: desc,
            inLanguage: "en-US",
            isPartOf: { "@id": "https://premierexecutivechauffeur.com#website" },
            about: { "@id": `${url}#service` },
            primaryImageOfPage: heroImage ? { "@id": `${url}#hero-image` } : undefined,
            breadcrumb: { "@id": `${url}#breadcrumb` },
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2"] },
          }),
        },
        ...(heroImage
          ? [{
              type: "application/ld+json" as const,
              children: ldJson({
                "@context": "https://schema.org",
                "@type": "ImageObject",
                creator: { "@type": "Organization", name: "Premier Executive Chauffeur", url: "https://premierexecutivechauffeur.com" },
                creditText: "Premier Executive Chauffeur",
                copyrightNotice: "© Premier Executive Chauffeur",
                copyrightHolder: { "@type": "Organization", name: "Premier Executive Chauffeur", url: "https://premierexecutivechauffeur.com" },
                license: "https://premierexecutivechauffeur.com/terms",
                acquireLicensePage: "https://premierexecutivechauffeur.com/contact",
                "@id": `${url}#hero-image`,
                url: heroImageUrl,
                contentUrl: heroImageUrl,
                caption: heroImage.alt ?? `${shortName} chauffeur pickup`,
                representativeOfPage: true,
              }),
            }]
          : []),
        {
          type: "application/ld+json",
          children: ldJson({
            "@context": "https://schema.org",
            "@type": "Service",
            "@id": `${url}#service`,
            name: shortTitle,
            description: desc,
            serviceType: "Private Aviation FBO Transfer",
            url,
            provider: { "@id": "https://premierexecutivechauffeur.com#business" },
            areaServed: {
              "@type": "Place",
              name: shortName,
              address: { "@type": "PostalAddress", addressLocality: f.city, addressRegion: "CA", addressCountry: "US" },
            },
            hoursAvailable: hoursSpec24x7,
          }),
        },
        { type: "application/ld+json", children: ldJson(faqJsonLd(fboFaqs(f.name, f.city))) },
        { type: "application/ld+json", children: ldJson(speakableJsonLd(url)) },
        ...(gallery
          ? [{ type: "application/ld+json" as const, children: ldJson(fboGalleryJsonLd(f.name, url, gallery)) }]
          : []),
      ],
    };
  },
  notFoundComponent: () => (
    <section className="container-x py-24 max-w-2xl">
      <h1 className="text-3xl mb-3">FBO not found</h1>
      <Link to="/airports" className="btn-outline">All FBOs</Link>
    </section>
  ),
  errorComponent: () => (
    <section className="container-x py-24 max-w-2xl">
      <h1 className="text-3xl mb-3">Something went wrong</h1>
      <Link to="/airports" className="btn-outline">Back to airports</Link>
    </section>
  ),
  component: FboDetail,
});

function FboDetail() {
  const { fbo: f } = Route.useLoaderData();
  const airport = AIRPORTS.find((a) => a.code === f.airport);
  const otherFbos = FBOS.filter((x) => x.slug !== f.slug);
  const featuredCities = CITIES.slice(0, 8);
  const faqs = fboFaqs(f.name, f.city);
  const relatedServices = SERVICES.filter((s) =>
    ["private-aviation", "corporate", "airport-transfers", "sprinter-groups"].includes(s.slug),
  );

  return (
    <>
      <section className="container-x py-20 md:py-28 max-w-4xl">
        <nav aria-label="Breadcrumb" className="eyebrow mb-4">
          <Link to="/" className="hover:text-gold-soft">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/airports" className="hover:text-gold-soft">Airports & FBOs</Link>
          <span className="mx-2">/</span>
          <span>{f.name}</span>
        </nav>
        <div className="flex items-center gap-3 mb-4">
          <Building2 className="h-6 w-6 text-gold" />
          <span className="eyebrow text-gold-soft">Private Aviation FBO · {f.city}, California · staffed dispatch</span>
        </div>
        <h1 className="text-4xl md:text-5xl mb-6">{f.name}</h1>
        <p className="text-lg text-muted-foreground mb-4 speakable">{f.blurb}</p>
        <p className="text-muted-foreground speakable">{f.long}</p>
        {FBO_EXTENDED_CONTENT[f.slug] && (
          <p className="text-muted-foreground mt-4 speakable">{FBO_EXTENDED_CONTENT[f.slug].intro}</p>
        )}
        <div className="flex flex-wrap gap-3 mt-8">
          <a href={SITE.bookUrl} target="_blank" rel="noopener noreferrer" className="btn-gold hover:btn-gold-hover">Reserve FBO chauffeur</a>
          <a href={SITE.phoneHref} className="btn-outline hover:bg-gold hover:text-primary-foreground inline-flex items-center gap-2"><Phone className="h-4 w-4" />Call {SITE.phone}</a>
        </div>
      </section>

      {FBO_GALLERY[f.slug] && (
        <section className="border-t border-border" aria-labelledby="fbo-gallery-heading">
          <div className="container-x py-16 max-w-5xl">
            <h2 id="fbo-gallery-heading" className="text-2xl mb-6">Inside {f.name}</h2>
            <GalleryLightbox images={FBO_GALLERY[f.slug]} heading={`Inside ${f.name}`} />
            <p className="text-xs text-muted-foreground mt-4">Tap any photo to enlarge.</p>
          </div>
        </section>
      )}

      {FBO_LOCATIONS[f.slug] && (() => {
        const loc = FBO_LOCATIONS[f.slug];
        return (
          <section className="border-t border-border bg-surface" aria-labelledby="fbo-map-heading">
            <div className="container-x py-16 max-w-5xl">
              <h2 id="fbo-map-heading" className="text-2xl mb-2">Where {f.name} is &amp; how to get there</h2>
              <p className="text-sm text-muted-foreground mb-6 flex items-center gap-2">
                <MapPin className="h-4 w-4 text-gold" aria-hidden />
                <span>{loc.address}</span>
              </p>
              <div className="grid gap-6 md:grid-cols-5">
                <div className="md:col-span-3 border border-border overflow-hidden bg-background">
                  <iframe
                    title={`Map of ${f.name}`}
                    src={fboMapEmbedUrl(loc)}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="w-full h-[360px] block border-0"
                    allowFullScreen
                  />
                </div>
                <div className="md:col-span-2 space-y-4">
                  <div>
                    <h3 className="text-lg mb-2">FBO gate &amp; approach</h3>
                    <p className="text-sm text-muted-foreground">{loc.exit}</p>
                  </div>
                  <a
                    href={fboDirectionsUrl(loc)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-gold hover:btn-gold-hover inline-flex items-center gap-2"
                  >
                    <Navigation className="h-4 w-4" aria-hidden />
                    Get driving directions
                  </a>
                </div>
              </div>
              <div className="mt-10">
                <h3 className="text-lg mb-4">Driving directions from popular pickup points</h3>
                <ul className="space-y-4">
                  {loc.routes.map((r) => (
                    <li key={r.from} className="border-l-2 border-gold pl-4">
                      <div className="text-foreground">{r.from}</div>
                      <p className="text-sm text-muted-foreground mt-1">{r.notes}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        );
      })()}


      {FBO_EXTENDED_CONTENT[f.slug] && (
        <section className="border-t border-border bg-surface">
          <div className="container-x py-16 max-w-4xl space-y-10">
            {FBO_EXTENDED_CONTENT[f.slug].blocks.map((b) => (
              <div key={b.heading}>
                <h2 className="text-2xl mb-3">{b.heading}</h2>
                <p className="text-muted-foreground">{b.body}</p>
              </div>
            ))}
            {FBO_EXTENDED_CONTENT[f.slug].bullets.map((group) => (
              <div key={group.title}>
                <h2 className="text-2xl mb-4">{group.title}</h2>
                <ul className="space-y-2 text-muted-foreground list-disc pl-6">
                  {group.points.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}

      {FBO_PICKUP[f.slug] && (() => {
        const p = FBO_PICKUP[f.slug];
        return (
          <section className="border-t border-border" aria-labelledby="fbo-pickup-heading">
            <div className="container-x py-16 max-w-4xl">
              <p className="eyebrow mb-2">Pickup &amp; curbside instructions</p>
              <h2 id="fbo-pickup-heading" className="text-2xl mb-4">
                Where to meet your chauffeur at {f.name}
              </h2>
              <p className="text-muted-foreground mb-8 speakable">{p.summary}</p>

              <div className="grid gap-6 md:grid-cols-2">
                <div className="border border-border p-5 bg-surface">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-5 w-5 text-gold" aria-hidden />
                    <h3 className="text-lg">Meeting point</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">{p.meetingPoint}</p>
                </div>
                <div className="border border-border p-5 bg-surface">
                  <div className="flex items-center gap-2 mb-2">
                    <Car className="h-5 w-5 text-gold" aria-hidden />
                    <h3 className="text-lg">Vehicle staging</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">{p.staging}</p>
                </div>
                <div className="border border-border p-5 bg-surface md:col-span-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Luggage className="h-5 w-5 text-gold" aria-hidden />
                    <h3 className="text-lg">Baggage handoff</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">{p.baggage}</p>
                </div>
              </div>

              <div className="mt-8">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldAlert className="h-5 w-5 text-gold" aria-hidden />
                  <h3 className="text-lg">Ground-handling constraints</h3>
                </div>
                <ul className="space-y-2 text-sm text-muted-foreground list-disc pl-6">
                  {p.constraints.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
              </div>

              {p.peakNote && (
                <p className="mt-6 text-sm text-muted-foreground border-l-2 border-gold pl-4">
                  <span className="text-foreground">Peak-window note: </span>
                  {p.peakNote}
                </p>
              )}
            </div>
          </section>
        );
      })()}

      <section className="border-t border-border bg-surface" aria-labelledby="fbo-flight-contact-heading">
        <div className="container-x py-16 max-w-4xl">
          <p className="eyebrow mb-3 text-gold">Flight details</p>
          <h2 id="fbo-flight-contact-heading" className="text-2xl mb-3">
            Email us about your {f.name} pickup
          </h2>
          <p className="text-sm text-muted-foreground mb-6 max-w-2xl">
            Include the flight or tail number, arrival date and time, passenger count, luggage, and an on-site contact.
          </p>
          <a
            href={`mailto:${SITE.email}?subject=${encodeURIComponent(`${f.name} chauffeur pickup`)}`}
            className="btn-gold hover:btn-gold-hover inline-flex items-center gap-2"
          >
            <Mail className="h-4 w-4" aria-hidden="true" />
            Email {SITE.email}
          </a>
        </div>
      </section>


      <section className="border-t border-border">
        <div className="container-x py-14 max-w-4xl">
          <h2 className="text-2xl mb-6">Chauffeur services from this FBO</h2>
          <div className="grid gap-3 md:grid-cols-2">
            {relatedServices.map((s) => (
              <Link
                key={s.slug}
                to="/services/$service"
                params={{ service: s.slug }}
                className="border border-border p-5 hover:border-gold transition-colors block"
              >
                <div className="text-foreground">{s.title}</div>
                <p className="text-xs text-muted-foreground mt-1">{s.short}</p>
                <span className="sr-only"> at {f.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {airport && (
        <section className="border-t border-border bg-surface">
          <div className="container-x py-14 max-w-4xl">
            <h2 className="text-2xl mb-4">Connected airport</h2>
            <Link
              to="/airports/$code"
              params={{ code: airport.slug }}
              className="border border-border bg-background p-5 flex items-start gap-4 hover:border-gold transition-colors group"
            >
              <Plane className="h-6 w-6 text-gold mt-1" />
              <div>
                <div className="flex items-baseline gap-3">
                  <span className="font-display text-2xl text-gold">{airport.code}</span>
                  <span className="text-foreground group-hover:text-gold-soft">{airport.name}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-2">{airport.blurb}</p>
              </div>
            </Link>
          </div>
        </section>
      )}

      <section className="border-t border-border">
        <div className="container-x py-16">
          <h2 className="text-2xl mb-6">Cities we serve from {f.name}</h2>
          <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-4">
            {featuredCities.map((c) => (
              <Link
                key={c.slug}
                to="/service-areas/$city"
                params={{ city: c.slug }}
                className="border border-border px-4 py-3 text-sm hover:border-gold transition-colors"
              >
                {c.name}
                <span className="block text-xs text-muted-foreground mt-0.5">{c.region}</span>
                <span className="sr-only"> transfers from {f.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-surface">
        <div className="container-x py-16">
          <h2 className="text-2xl mb-4">Other Bay Area FBOs</h2>
          <div className="grid gap-3 md:grid-cols-2">
            {otherFbos.map((x) => (
              <Link
                key={x.slug}
                to="/fbos/$slug"
                params={{ slug: x.slug }}
                className="border border-border bg-background px-5 py-4 hover:border-gold transition-colors"
              >
                <div className="text-foreground">{x.name}</div>
                <p className="text-xs text-muted-foreground mt-1">{x.blurb}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="container-x py-16 max-w-3xl speakable">
          <p className="eyebrow mb-2">Hours & timing</p>
          <h2 className="text-2xl mb-6">{f.name} FAQ</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Reservations are accepted online around the clock and dispatch is staffed daily. Vehicles are staged at {f.name} coordinated with your flight crew or dispatcher, and last-minute FBO requests are typically covered within 60 minutes.
          </p>
          <div className="space-y-3">
            {faqs.map((q) => (
              <details key={q.q} className="border border-border p-5 group">
                <summary className="cursor-pointer text-foreground group-open:text-gold">{q.q}</summary>
                <p className="mt-3 text-sm text-muted-foreground">{q.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <CrossLinkModule context={{ type: "fbo", slug: f.slug }} />

      <CTABand heading={`Reserve your ${f.city} FBO chauffeur`} sub={`Flight-tracked, pre-arranged, and dispatched daily. Call ${SITE.phone}.`} />
    </>
  );
}
